import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';
import { IState, IStatesResponse } from '../models/states.model';
import { IDocumentContentResponse } from '../models/documents.model';

const US_STATES_KEY = makeStateKey<IState[]>('us-states');

@Injectable({
    providedIn: 'root'
})
export class UsStatesService {
    readonly usStates = signal<IState[]>([]);
    readonly currentStateDetails = signal<IState | null>(null);

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        private tenantService: TenantService,
        @Inject(API_BASE_URL) private apiBaseUrl: string
    ) {}

    async loadUsStates(): Promise<void> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('uslegalization.eu') || domain.includes("paymentinfo.uslegalization.com")) {
            domain = environment.fallbackDomain;
        }

        // 1. Browser: reuse server-rendered data from TransferState
        if (this.transferState.hasKey(US_STATES_KEY)) {
            const cached = this.transferState.get(US_STATES_KEY, []);
            this.usStates.set(cached);
            this.transferState.remove(US_STATES_KEY);
            return;
        }

        // 2. Fetch via internal proxy
        try {
            const response = await firstValueFrom(
                this.http.get<IStatesResponse>(
                    `${this.apiBaseUrl}/internal-api/${domain}/us-states/all`
                ).pipe(
                    timeout(5000),
                    catchError((err) => {
                        console.error('[UsStates] HTTP error:', err);
                        return of({ data: [] } as IStatesResponse);
                    })
                )
            );

            const freshData = response?.data || [];
            this.usStates.set(freshData);

            // 3. Save to TransferState on the server so the browser can reuse it
            if (isPlatformServer(this.platformId)) {
                this.transferState.set(US_STATES_KEY, freshData);
            }
        } catch (error) {
            console.error(`[UsStates] Failed to load for domain: ${domain}`, error);
            this.usStates.set([]);
        }
    }

    async loadStateDetails(stateCode: string): Promise<void> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('uslegalization.eu') || domain.includes("paymentinfo.uslegalization.com")) {
            domain = environment.fallbackDomain;
        }

        const stateDetailsKey = makeStateKey<IState>(`state-details-${stateCode.toLowerCase()}`);

        if (this.transferState.hasKey(stateDetailsKey)) {
            const cached = this.transferState.get(stateDetailsKey, null);
            this.currentStateDetails.set(cached);
            this.transferState.remove(stateDetailsKey);
            return;
        }

        if (this.usStates().length === 0) {
            await this.loadUsStates();
        }

        const basicStateInfo = this.usStates().find(s => s.code.toLowerCase() === stateCode.toLowerCase() || s.slug.toLowerCase() === stateCode.toLowerCase());
        
        if (!basicStateInfo) {
            console.error(`[UsStates] State not found for code: ${stateCode}`);
            this.currentStateDetails.set(null);
            return;
        }

        const stateId = basicStateInfo.id;

        try {
            const response = await firstValueFrom(
                this.http.get<any>(
                    `${this.apiBaseUrl}/internal-api/${domain}/us-states/state/${stateId}`
                ).pipe(
                    timeout(5000),
                    catchError((err) => {
                        console.error('[UsStates] HTTP error loading state details:', err);
                        return of(null);
                    })
                )
            );

            const freshData = response?.data ? response.data : response;

            this.currentStateDetails.set(freshData || null);

            if (freshData && isPlatformServer(this.platformId)) {
                this.transferState.set(stateDetailsKey, freshData);
            }
        } catch (error) {
            console.error(`[UsStates] Failed to load details for state: ${stateCode}`, error);
            this.currentStateDetails.set(null);
        }
    }

    async getStatesPageSeo(): Promise<IDocumentContentResponse | null> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('uslegalization.eu') || domain.includes("paymentinfo.uslegalization.com")) {
            domain = environment.fallbackDomain;
        }

        const cacheKey = makeStateKey<IDocumentContentResponse>(`states-seo-${domain}`);

        if (this.transferState.hasKey(cacheKey)) {
            const cached = this.transferState.get(cacheKey, null);
            this.transferState.remove(cacheKey);
            return cached;
        }

        try {
            const response = await firstValueFrom(
                this.http.get<IDocumentContentResponse>(
                    `${this.apiBaseUrl}/internal-api/document-content/${domain}/states`
                ).pipe(
                    timeout(5000),
                    catchError((err) => {
                        console.error('[UsStates] HTTP error loading states SEO:', err);
                        return of(null);
                    })
                )
            );

            if (response && isPlatformServer(this.platformId)) {
                this.transferState.set(cacheKey, response);
            }
            return response;
        } catch (error) {
            console.error('[UsStates] Failed to load states SEO', error);
            return null;
        }
    }

    async getPageSeoBySlug(slug: string): Promise<IDocumentContentResponse | null> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('uslegalization.eu') || domain.includes("paymentinfo.uslegalization.com")) {
            domain = environment.fallbackDomain;
        }

        const cacheKey = makeStateKey<IDocumentContentResponse>(`page-seo-${slug}-${domain}`);

        if (this.transferState.hasKey(cacheKey)) {
            const cached = this.transferState.get(cacheKey, null);
            this.transferState.remove(cacheKey);
            return cached;
        }

        try {
            const response = await firstValueFrom(
                this.http.get<IDocumentContentResponse>(
                    `${this.apiBaseUrl}/internal-api/document-content/${domain}/${slug}`
                ).pipe(
                    timeout(5000),
                    catchError((err) => {
                        console.error(`[UsStates] HTTP error loading SEO for ${slug}:`, err);
                        return of(null);
                    })
                )
            );

            if (response && isPlatformServer(this.platformId)) {
                this.transferState.set(cacheKey, response);
            }
            return response;
        } catch (error) {
            console.error(`[UsStates] Failed to load SEO for ${slug}:`, error);
            return null;
        }
    }
}
